// Thin wrapper around pdf.js. Keeps the worker setup in one place and loads the
// library lazily so it never weighs down the initial bundle / cold start.
//
// pdf.js 4.x ships ESM-only (no more `pdf.worker.entry` CJS loader), so the
// worker is wired up via `new URL(..., import.meta.url)` — webpack 5 turns
// that into an emitted asset URL, and it still works fully offline (no CDN),
// which matters for a desktop app.
import type { PDFDocumentProxy } from "pdfjs-dist";

type PdfjsModule = typeof import("pdfjs-dist");

let pdfjsPromise: Promise<PdfjsModule> | null = null;

/** Lazily import pdf.js and wire up its worker exactly once. */
export const getPdfjs = async (): Promise<PdfjsModule> => {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import("pdfjs-dist");
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url,
      ).toString();
      return pdfjs;
    })();
  }
  return pdfjsPromise;
};

/** Load a PDF from raw bytes into a pdf.js document proxy. Pass `password`
 *  for an encrypted PDF; omitting it on an encrypted file rejects with a
 *  `PasswordException` (see `isPasswordException` below). */
export const loadPdfDocument = async (
  data: Uint8Array | ArrayBuffer,
  password?: string,
): Promise<PDFDocumentProxy> => {
  const pdfjs = await getPdfjs();
  // pdf.js transfers/detaches the buffer, so hand it a private copy to avoid
  // clobbering bytes the caller may reuse (e.g. for a later client-side edit).
  const bytes =
    data instanceof Uint8Array ? data.slice() : new Uint8Array(data).slice();
  const task = pdfjs.getDocument({ data: bytes, password });
  return task.promise;
};

/** pdf.js's PasswordException shape. Not re-exported in pdfjs-dist's type
 *  declarations, so we duck-type against its stable runtime contract instead
 *  of reaching into pdfjs internals. */
export interface PasswordExceptionLike {
  name: "PasswordException";
  /** 1 = password needed, 2 = the supplied password was wrong. */
  code: 1 | 2;
}

export const isPasswordException = (
  err: unknown,
): err is PasswordExceptionLike =>
  !!err &&
  typeof err === "object" &&
  (err as { name?: unknown }).name === "PasswordException";

export const isIncorrectPassword = (err: PasswordExceptionLike): boolean =>
  err.code === 2;
